import React, { useContext, useEffect, useState } from "react";
import PatientDetails from "../../Components/PatientInformation";
import ErrorField from "../../Components/ErrorField";
import {
  AddLocalStorageData,
  DateInRange,
  GetLocalStorageData,
  GetLoggedInUserID,
  IsAdmin,
  IsLessOrEqual,
  IsValidDateValue,
} from "../../Helpers/Utilities";
import {
  SideMenuItems,
  HTTPResponse,
  ToastMessageType,
  ToastMessages,
} from "../../Helpers/Enums";
import {
  CommonError_Msgs,
  EligibilityCriteria_Msgs,
} from "../../Helpers/HelperText";
import { AppContext } from "../../Contexts/AppContextProvider";
import DatePickerComponent from "../../Components/DatePickerComponent";
import dayjs from "dayjs";
import SaveAndNavigate from "../../Components/SaveAndNavigate";
import SideBarMenu from "../../Components/SideBarMenu";
import { NDMMTreatmentCohortService } from "../../WebApiServices/NDMMTreatmentCohortService.service";
import { BispecificsCartTreatmentCohortService } from "../../WebApiServices/BispecificsCartTreatmentCohort.service";

const groupAndSortByCategory = (agents) => {
  const grouped = agents.reduce((acc, item) => {
    const category = item.agentCategory || "Other";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  Object.keys(grouped).forEach((category) => {
    grouped[category].sort((a, b) => a.sortOrder - b.sortOrder);
  });

  const sortedEntries = Object.entries(grouped).sort(
    ([, agentsA], [, agentsB]) => {
      const minSortOrderA = Math.min(...agentsA.map((a) => a.sortOrder));
      const minSortOrderB = Math.min(...agentsB.map((a) => a.sortOrder));
      return minSortOrderA - minSortOrderB;
    },
  );

  return Object.fromEntries(sortedEntries);
};

const sortAgents = (agents) => {
  return agents.sort((a, b) => a.sortOrder - b.sortOrder);
};

const allRowsHaveAgents = (agentRows) => {
  return agentRows.every((row) => row.agentid !== null && row.agentid !== "");
};

const isOtherAgentSelected = (agentId, agentList) => {
  if (!agentId || !agentList) return false;

  const selectedAgent = agentList.find((agent) => agent.agentid === agentId);
  if (!selectedAgent) return false;

  return selectedAgent.agentName?.toLowerCase().includes("other");
};

// earliest of all known initiation dates for this treatment
const getEarliestTreatmentInitiationDate = (treatment) => {
  const knownDates = (treatment.agentRows || [])
    .filter(
      (row) =>
        row.initiationDateRadio === 1 &&
        row.initiationDate &&
        String(row.initiationDate).trim() !== "",
    )
    .map((row) => dayjs(row.initiationDate));

  if (knownDates.length === 0) return null;

  return knownDates.reduce((min, d) => (d.isBefore(min) ? d : min));
};

// Index date = earliest of (cohortIndexDate, earliestInitiationDate)
const computeIndexDate = (treatment) => {
  const baseIndexDate =
    treatment.cohortIndexDate && String(treatment.cohortIndexDate).trim() !== ""
      ? dayjs(treatment.cohortIndexDate)
      : null;

  const earliestInitiationDate = getEarliestTreatmentInitiationDate(treatment);

  if (baseIndexDate && earliestInitiationDate) {
    const earliest = earliestInitiationDate.isBefore(baseIndexDate)
      ? earliestInitiationDate
      : baseIndexDate;
    return earliest.format("MM/DD/YYYY");
  }

  if (baseIndexDate) return baseIndexDate.format("MM/DD/YYYY");
  if (earliestInitiationDate)
    return earliestInitiationDate.format("MM/DD/YYYY");
  return "";
};

const BispecificInitiation = () => {
  const {
    ShowToast,
    ToggleLoader,
    HandleSessionTimeout,
    isSideMenuActive,
    setIsSideMenuActive,
    patientConfigData,
    ShowActionPopUp,
  } = useContext(AppContext);

  const [showTopErrMsg, setShowTopErrMsg] = useState(false);
  const [agentList, setAgentList] = useState([]);
  const [terminate, setTerminate] = useState(false);
  const [treatmentErrors, setTreatmentErrors] = useState({});
  // per‑cohort soft terminate flags (for warning + termination on save)
  const [cohortTerminate, setCohortTerminate] = useState({});

  // Eligibility states (like RRMM)
  const [isEligible, setIsEligible] = useState(true);
  const [isAllDataFilled, setIsAllDataFilled] = useState(false);
  const [eligibilityCriteriaLocked, setEligibilityCriteriaLocked] =
    useState(false);

  // dates from eligibility model
  const [eligibilityDates, setEligibilityDates] = useState({
    dateOfBirth: null,
    ndmmIndexDate: null, // InitiationDate1LTreatment
    initialMMDiagnosisDate: null,
  });

  // global eligibility warning flag
  const [eligibilityWarning, setEligibilityWarning] = useState(false);

  const rowTemplate = {
    rowId: 1,
    agentid: null,
    agentOtherValue: "",
    initiationDate: "",
    initiationDateRadio: null,
    agentError: false,
    dateError: false,
    invalidDateError: false,
  };

  const [bispecificTreatments, setBispecificTreatments] = useState([]);
  const pid = GetLocalStorageData("patientId") ?? 0;

  const terminatedPageId = patientConfigData?.data?.terminatedPageId;
  const navigatePaths = {
    prevPage: "/RRMMCriteria",
    currPage: "/RRMMComboTherapy",
    nextPage:
      terminatedPageId === SideMenuItems.RRMMComboTherapy
        ? "/ManagePatients"
        : (isAllDataFilled || eligibilityCriteriaLocked) && isEligible
          ? "/ExclusionCriteria"
          : isAllDataFilled && !isEligible
            ? "/ManagePatients"
            : "/RRMMComboTherapy",
  };

  useEffect(() => {
    async function FetchData() {
      if (!isNaN(pid) && Number(pid) !== 0) {
        ToggleLoader(true);
        await LoadData();
        AddLocalStorageData("loadConfig", true);
        ToggleLoader(false);
      }
    }
    FetchData();
  }, []);

  const LoadData = async () => {
    try {
      if (pid > 0) {
        const response = await NDMMTreatmentCohortService.GetTableData(pid);

        if (response?.status === HTTPResponse.OK) {
          if (
            response.data?.q24_agentLists &&
            response.data.q24_agentLists.length > 0
          ) {
            setAgentList(response.data.q24_agentLists);
          }

          const storedData = GetLocalStorageData("CohortTreatment");
          if (storedData) {
            let cohortTreatmentList = JSON.parse(storedData);

            const isAdmin = false;
            let eligibilityDates = {};

            const savedDataResponse =
              await BispecificsCartTreatmentCohortService.GetBispecificInitiationData(
                pid,
                isAdmin,
              );
            let savedData = {};
            let hasSavedBispecificData = false;
            let bispecificDetails = [];
            if (savedDataResponse?.status === HTTPResponse.OK) {
              const responseData = savedDataResponse.data;

              setEligibilityCriteriaLocked(
                responseData?.eligibilityLocked ?? false,
              );
              if (responseData.eligibleCohortExists) {
                setTerminate(
                  responseData.eligibleCohortExists === true ? false : true,
                );
              }

              eligibilityDates = {
                dateOfBirth:
                  responseData?.dateOfBirth ||
                  responseData?.DateOfBirth ||
                  null,
                ndmmIndexDate:
                  responseData?.initiationDate1LTreatment ||
                  responseData?.InitiationDate1LTreatment ||
                  null,
                initialMMDiagnosisDate:
                  responseData?.initialMMDiagnosisDate ||
                  responseData?.InitialMMDiagnosisDate ||
                  null,
              };
              setEligibilityDates(eligibilityDates);

              bispecificDetails = responseData?.bispecificDetails || [];

              if (
                bispecificDetails &&
                Array.isArray(bispecificDetails) &&
                bispecificDetails.length > 0
              ) {
                hasSavedBispecificData = bispecificDetails.some((detail) => {
                  const agents =
                    detail.agents || detail.Agents || detail.agentRows || [];
                  return agents.length > 0;
                });

                savedData = bispecificDetails.reduce((acc, treatment) => {
                  const cohortId =
                    treatment.cohortTreatmentID ||
                    treatment.CohortTreatmentID ||
                    treatment.cohorttreatmentid;

                  const agents =
                    treatment.agents ||
                    treatment.Agents ||
                    treatment.agentRows ||
                    [];

                  if (cohortId) {
                    acc[cohortId] = agents;
                  }
                  return acc;
                }, {});
              }
            }

            if (hasSavedBispecificData) {
              // Show all RRMM cohorts (ndmm=false) when there's saved data
              cohortTreatmentList = cohortTreatmentList.filter(
                (c) => c.ndmm !== true && c.monoTherapyReceived === false,
              );

              // Get cohort IDs that are in savedData
              const savedCohortIds = Object.keys(savedData).map((id) =>
                parseInt(id),
              );

              const existingCohortIds = cohortTreatmentList.map(
                (c) => c.cohortTreatmentID,
              );

              // Find missing cohort IDs (have saved data but not in current list)
              const missingCohortIds = savedCohortIds.filter(
                (id) => !existingCohortIds.includes(id),
              );

              // ✅ For missing cohorts, build from bispecificDetails
              if (missingCohortIds.length > 0 && bispecificDetails) {
                bispecificDetails.forEach((detail) => {
                  const cohortId =
                    detail.cohortTreatmentID ||
                    detail.CohortTreatmentID ||
                    detail.cohorttreatmentid;

                  if (
                    missingCohortIds.includes(cohortId) &&
                    cohortId >= 9 &&
                    cohortId <= 14
                  ) {
                    //  Build cohort object from bispecificDetails
                    cohortTreatmentList.push({
                      cohortTreatmentID: cohortId,
                      cohortTreatmentName:
                        detail.cohortTreatmentName ||
                        detail.CohortTreatmentName ||
                        getCohortNameById(cohortId), // fallback to static mapping
                      cohortTreatmentNameSuperscript:
                        detail.cohortTreatmentNameSuperscript ||
                        detail.CohortTreatmentNameSuperscript ||
                        "",
                      cohortIndexDate:
                        detail.cohortIndexDate || detail.CohortIndexDate || "",
                      isEligible: false,
                      ndmm: false,
                      monoTherapyReceived: true,
                    });
                  }
                });
              }
            } else {
              // Show only combination cohorts when no saved data
              cohortTreatmentList = cohortTreatmentList.filter(
                (c) => c.ndmm !== true && c.monoTherapyReceived == false,
              );
            }

            const uniqueCohortList = cohortTreatmentList.reduce(
              (acc, current) => {
                const exists = acc.find(
                  (item) =>
                    item.cohortTreatmentID === current.cohortTreatmentID,
                );
                if (!exists) {
                  acc.push(current);
                }
                return acc;
              },
              [],
            );

            const initialData = uniqueCohortList.map((treatment) => {
              const savedAgents = savedData[treatment.cohortTreatmentID] || [];

              const baseTreatment = {
                cohortTreatmentID: treatment.cohortTreatmentID,
                cohortTreatmentName: treatment.cohortTreatmentName,
                superScriptText: treatment.cohortTreatmentNameSuperscript,
                cohortIndexDate: treatment.cohortIndexDate,
                agentRows:
                  savedAgents.length > 0
                    ? savedAgents.map((agent, idx) => {
                        return {
                          rowId: idx + 1,
                          agentid: agent.agentid || agent.Agentid,
                          agentOtherValue:
                            agent.agentOtherValue ||
                            agent.AgentOtherValue ||
                            "",
                          initiationDate:
                            agent.initiationDate || agent.InitiationDate || "",
                          initiationDateRadio:
                            agent.initiationDateRadio ||
                            agent.InitiationDateRadio,
                          agentError: false,
                          dateError: false,
                          invalidDateError: false,
                          rrmmtreatmentagentbridgeid:
                            agent.rrmmtreatmentagentbridgeid ||
                            agent.Rrmmtreatmentagentbridgeid,
                        };
                      })
                    : [],
              };

              return {
                ...baseTreatment,
                computedIndexDate: computeIndexDate(baseTreatment),
              };
            });

            setBispecificTreatments(initialData);
            // After loading, recompute terminate flags for all cohorts
            initialData.forEach((itm, idx) => {
              recomputeTerminateForTreatment(idx, itm, eligibilityDates);
            });

            if (savedDataResponse.data.cohortTreatmentModelList.length > 0)
              AddLocalStorageData(
                "CohortTreatment",
                JSON.stringify(savedDataResponse.data.cohortTreatmentModelList),
              );
            // else AddLocalStorageData("CohortTreatment", JSON.stringify([]));
          }
        } else if (response?.status === HTTPResponse.Unauthorized) {
          HandleSessionTimeout();
        } else {
          throw response?.error;
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      ShowToast(CommonError_Msgs.LOAD_ERR, ToastMessageType.Failed);
    }
  };

  const recalcComputedIndexForTreatment = (treatments, treatmentIdx) => {
    const t = treatments[treatmentIdx];
    treatments[treatmentIdx] = {
      ...t,
      computedIndexDate: computeIndexDate(t),
    };
  };

  const recomputeTerminateForTreatment = (
    treatmentIdx,
    treatment,
    eligibilityDates,
  ) => {
    const { dateOfBirth, ndmmIndexDate, initialMMDiagnosisDate } =
      eligibilityDates;

    let terminate = false;

    // Unknown date in any agent
    if (treatment.agentRows.some((r) => r.initiationDateRadio === 99)) {
      terminate = true;
    }

    const cohortDate = !IsValidDateValue(treatment.computedIndexDate)
      ? treatment.computedIndexDate
      : null;

    if (!cohortDate) {
      terminate = false;
    } else {
      const birthDate = IsValidDateValue(dateOfBirth) ? dateOfBirth : null;
      const diagnosisDate = IsValidDateValue(initialMMDiagnosisDate)
        ? initialMMDiagnosisDate
        : null;
      const ndmmDate = IsValidDateValue(ndmmIndexDate) ? ndmmIndexDate : null;

      if (birthDate && !IsLessOrEqual(birthDate, cohortDate)) {
        terminate = true;
      }

      if (diagnosisDate && !IsLessOrEqual(diagnosisDate, cohortDate)) {
        terminate = true;
      }

      if (ndmmDate && !IsLessOrEqual(ndmmDate, cohortDate)) {
        terminate = true;
      }
    }

    // Age validation (only when both dates exist)
    if (dateOfBirth && treatment.computedIndexDate) {
      const ageYears = dayjs(treatment.computedIndexDate).diff(
        dayjs(dateOfBirth),
        "year",
      );

      if (ageYears < 18) {
        terminate = true;
      }
    }

    setCohortTerminate((prev) => ({ ...prev, [treatmentIdx]: terminate }));

    // Update global eligibility warning
    const updatedTerminate = { ...cohortTerminate, [treatmentIdx]: terminate };
    setEligibilityWarning(Object.values(updatedTerminate).some((v) => v));
  };

  const addAgentRow = (treatmentIdx) => {
    let updatedArray = [...bispecificTreatments];
    const currentRows = updatedArray[treatmentIdx].agentRows;
    const newRowId =
      currentRows.length > 0
        ? Math.max(...currentRows.map((r) => r.rowId)) + 1
        : 1;

    updatedArray[treatmentIdx].agentRows.push(
      JSON.parse(
        JSON.stringify({
          ...rowTemplate,
          rowId: newRowId,
        }),
      ),
    );

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);

    setTreatmentErrors((prev) => ({
      ...prev,
      [treatmentIdx]: false,
    }));
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(treatmentIdx, treatment, eligibilityDates);
  };

  const deleteAgentRow = (treatmentIdx, rowIdx) => {
    let updatedArray = [...bispecificTreatments];
    updatedArray[treatmentIdx].agentRows.splice(rowIdx, 1);
    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(treatmentIdx, treatment, eligibilityDates);
  };

  const handleAgentChange = (treatmentIdx, rowIdx, agentId) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.agentid = agentId;
    row.agentOtherValue = "";
    row.agentError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(treatmentIdx, treatment, eligibilityDates);
  };

  const handleAgentOtherChange = (treatmentIdx, rowIdx, value) => {
    let updatedArray = [...bispecificTreatments];
    updatedArray[treatmentIdx].agentRows[rowIdx].agentOtherValue = value;
    updatedArray[treatmentIdx].agentRows[rowIdx].agentError = false;
    setBispecificTreatments(updatedArray);
  };

  const handleDateChange = (treatmentIdx, rowIdx, date) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.initiationDate = date;
    row.initiationDateRadio = 1;
    row.dateError = false;
    row.invalidDateError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(treatmentIdx, treatment, eligibilityDates);
  };

  const handleDateRadioChange = (treatmentIdx, rowIdx, value) => {
    let updatedArray = [...bispecificTreatments];
    const row = updatedArray[treatmentIdx].agentRows[rowIdx];

    row.initiationDateRadio = value;
    if (value === 99) {
      row.initiationDate = "";
    }
    row.dateError = false;
    row.invalidDateError = false;

    recalcComputedIndexForTreatment(updatedArray, treatmentIdx);
    setBispecificTreatments(updatedArray);
    const treatment = updatedArray[treatmentIdx];
    recomputeTerminateForTreatment(treatmentIdx, treatment, eligibilityDates);
  };

  const getAvailableAgents = (treatmentIdx, currentRowIdx) => {
    const selectedAgentIds = bispecificTreatments[treatmentIdx].agentRows
      // .map((row, idx) =>
      //   idx !== currentRowIdx && row.agentid ? row.agentid : null
      // )
      .map((row, idx) => {
        if (idx !== currentRowIdx && row.agentid) {
          const agent = agentList.find((a) => a.agentid === row.agentid);
          const isOther = agent?.agentName?.toLowerCase().includes("other");
          return isOther ? null : row.agentid;
        }
        return null;
      })

      .filter((id) => id !== null);

    return agentList.filter(
      (agent) => !selectedAgentIds.includes(agent.agentid),
    );
  };

  const getGroupedAgents = (treatmentIdx, currentRowIdx) => {
    const availableAgents = getAvailableAgents(treatmentIdx, currentRowIdx);
    return groupAndSortByCategory(availableAgents);
  };

  const getAvailableAgentsForNextRow = (treatmentIdx) => {
    const selectedAgentIds = bispecificTreatments[treatmentIdx].agentRows
      .map((row) => (row.agentid ? row.agentid : null))
      .filter((id) => id !== null);

    return agentList.filter(
      (agent) => !selectedAgentIds.includes(agent.agentid),
    ).length;
  };

  const canShowAddMore = (treatmentIdx) => {
    const agentRows = bispecificTreatments[treatmentIdx].agentRows;

    if (agentRows.length === 0) return true;

    const availableAgentsCount = getAvailableAgentsForNextRow(treatmentIdx);
    return allRowsHaveAgents(agentRows) && availableAgentsCount > 0;
  };

  const ValidateForm = () => {
    let isValid = true;
    let updatedArray = [...bispecificTreatments];
    let errors = {};

    updatedArray.forEach((treatment, treatmentIdx) => {
      if (treatment.agentRows.length === 0) {
        errors[treatmentIdx] = true;
        isValid = false;
        return;
      }

      treatment.agentRows.forEach((row, rowIdx) => {
        // Hard validation: agent must be selected
        if (!row.agentid) {
          updatedArray[treatmentIdx].agentRows[rowIdx].agentError = true;
          isValid = false;
        }

        // Hard validation: if "Other" is selected, must specify
        if (
          isOtherAgentSelected(row.agentid, agentList) &&
          !row.agentOtherValue.trim()
        ) {
          updatedArray[treatmentIdx].agentRows[rowIdx].agentError = true;
          isValid = false;
        }

        // Hard validation: date radio must be selected
        if (!row.initiationDateRadio) {
          updatedArray[treatmentIdx].agentRows[rowIdx].dateError = true;
          isValid = false;
        }

        // Hard validation: if radio is 1 (known date), date must be provided
        if (row.initiationDateRadio === 1 && !row.initiationDate) {
          updatedArray[treatmentIdx].agentRows[rowIdx].dateError = true;
          isValid = false;
        }
        if (row.initiationDateRadio === 1 && row.initiationDate) {
          const date = dayjs(row.initiationDate);

          if (!DateInRange(row.initiationDate, dayjs("01-01-1920"), dayjs())) {
            updatedArray[treatmentIdx].agentRows[rowIdx].invalidDateError =
              true;
            isValid = false;
          }
        }
      });
    });

    setBispecificTreatments(updatedArray);
    setTreatmentErrors(errors);

    if (!isValid) {
      setShowTopErrMsg(true);
      setTimeout(() => setShowTopErrMsg(false), 5000);
    }

    setIsAllDataFilled(isValid);
    return isValid;
  };

  const ShowAlertBox = async () => {
    const choice = await ShowActionPopUp(
      "Alert", //title
      CommonError_Msgs.DateChangeAlert, //message
      "Ok", //Confirm button text
      true,
    );
  };

  const SavePage = async (validate) => {
    try {
      ToggleLoader(true);

      // On validate, run validation for hard errors only
      let isValid = true;
      if (validate) {
        isValid = ValidateForm();
        // If hard validation fails (missing fields, etc.), block save
        if (!isValid) {
          setShowTopErrMsg(true);
          ToggleLoader(false);
          return false;
        }
      }
      setShowTopErrMsg(false);

      const metInclusionCriteria = calculateEligibility();

      setIsEligible(metInclusionCriteria);
      const payload = {
        patientid: pid,
        UserId: GetLoggedInUserID(),
        isAdmin: IsAdmin(),
        isValidated: validate,
        // Send eligibility flag to backend (like RRMM)
        metInclusionCriteria: metInclusionCriteria,
        bispecificDetails: bispecificTreatments.map((treatment, idx) => ({
          cohortTreatmentID: treatment.cohortTreatmentID,
          metEligibilityCriteria: !cohortTerminate[idx],
          agents: treatment.agentRows.map((row) => ({
            rrmmtreatmentagentbridgeid: row.rrmmtreatmentagentbridgeid || null,
            agentid: row.agentid,
            agentOtherValue: row.agentOtherValue,
            initiationDate: row.initiationDate,
            initiationDateRadio: row.initiationDateRadio,
          })),
        })),
      };

      const saveResponse =
        await BispecificsCartTreatmentCohortService.SaveBispecificInitiationData(
          payload,
        );

      if (saveResponse?.status === HTTPResponse.OK) {
        ShowToast(ToastMessages.Save_Success, ToastMessageType.Success);
        // Reload to get updated terminatedPageId from backend
        await LoadData();
        ToggleLoader(false);

        if (saveResponse?.data?.showDateChangeAffectAlert) {
          ShowAlertBox();
        }

        if (metInclusionCriteria === true) {
          if (terminatedPageId === SideMenuItems.RRMMComboTherapy) {
            navigatePaths.nextPage = "/ManagePatients";
          } else {
            navigatePaths.nextPage = "/ExclusionCriteria";
          }
        }
        return true;
      } else {
        throw new Error("Save failed");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      ToggleLoader(false);
      ShowToast(CommonError_Msgs.SAVE_ERR, ToastMessageType.Failed);
      return false;
    }
  };

  const calculateEligibility = () => {
    const storedData = GetLocalStorageData("CohortTreatment");
    let eligibleCohortIds = [];

    if (storedData) {
      const cohortTreatmentList = JSON.parse(storedData);

      // NDMM eligible → immediately eligible
      const hasEligibleNdmm = cohortTreatmentList.some(
        (c) => c.ndmm === true && c.isEligible === true,
      );

      if (hasEligibleNdmm) {
        return true;
      }

      // Get eligible RRMM cohort IDs
      eligibleCohortIds = cohortTreatmentList
        .filter((c) => c.isEligible === true && !c.ndmm)
        .map((c) => c.cohortTreatmentID);
    }

    // If no eligible RRMM cohorts
    if (eligibleCohortIds.length === 0) {
      return false;
    }

    // Check if ALL eligible RRMM treatments are terminated
    const allEligibleTreatmentsTerminated =
      eligibleCohortIds.length > 0 &&
      eligibleCohortIds.every((cohortId) => {
        const idx = bispecificTreatments.findIndex(
          (t) => t.cohortTreatmentID === cohortId,
        );

        // If no bispecific record yet → NOT terminated
        if (idx === -1) return false;

        return cohortTerminate[idx] === true;
      });

    // Eligible if at least one treatment is active
    return !allEligibleTreatmentsTerminated;
  };

  const toggleNav = () => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive) {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
      setIsSideMenuActive(false);
    } else {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
      setIsSideMenuActive(true);
    }
  };

  useEffect(() => {
    const sidebar = document.getElementById("mySidebar");
    const main = document.getElementById("main");

    if (isSideMenuActive) {
      sidebar.style.width = "350px";
      main.style.marginLeft = "350px";
    } else {
      sidebar.style.width = "0";
      main.style.marginLeft = "0";
    }
  }, [isSideMenuActive]);

  function getCohortNameById(cohortId) {
    const cohortNames = {
      9: "TECVAYLI",
      10: "TALVEY",
      11: "ELREXFIO",
      12: "Linvoseltamab",
      13: "CARVYKTI",
      14: "ABECMA",
    };
    return cohortNames[cohortId] || `Cohort ${cohortId}`;
  }
  return (
    <>
      <section className="survey-patient-details-section">
        <div className="row">
          <PatientDetails />
          <div className="col-12 col-sm-5 col-md-5 col-lg-6 col-content-alignment">
            <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
          </div>
        </div>
      </section>

      <section className="main-survey-section">
        <div>
          <SideBarMenu activePageId={SideMenuItems.RRMMComboTherapy} />

          <button
            className={isSideMenuActive ? "openbtn" : "openbtn active"}
            onClick={toggleNav}
          >
            <i
              id="arrowNavigation"
              className="fa fa-arrow-left"
              aria-hidden="true"
            ></i>
          </button>

          <div className="col-pad-left eligibiltylocked" id="main">
            <div className="survey-section-content">
              <div className="survey-section-content-header">
                <h4>RRMM COMBO THERAPY</h4>
              </div>

              {!showTopErrMsg && pid != 0 && !isEligible && isAllDataFilled && (
                <div className="clean-error">
                  <p>{EligibilityCriteria_Msgs.IneligiblePatient}</p>
                </div>
              )}
              {showTopErrMsg && (
                <div className="clean-error">
                  <ErrorField
                    show={showTopErrMsg}
                    messageAnswer
                    message={CommonError_Msgs.Hard_Empty}
                  />
                </div>
              )}
              <div
                className="survey-question-section"
                disabled={eligibilityCriteriaLocked}
              >
                <div className="survey-question-content">
                  <div>
                    <div
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "16px",
                        lineHeight: "1.6",
                        fontWeight: "500",
                      }}
                      className="d-flex gap-1"
                    >
                      <span>S12.1.</span>
                      <span>
                        Please provide the date of combination initiation for
                        each agent as part of the Bispecific/CAR-T treatment.
                        Add additional rows for each agent received during that
                        line of therapy. Please note the following:
                      </span>
                    </div>
                    <ol
                      style={{
                        margin: "0",
                        paddingLeft: "50px",
                        fontSize: "16px",
                        listStyleType: "lower-alpha",
                      }}
                    >
                      <li style={{ marginBottom: "8px" }}>
                        Please list one agent per row. If the <em>"Other"</em>{" "}
                        field is used to provide an agent not within the
                        pre-specified fields, please ensure another new row
                        corresponds to any other agent in the <em>"Other"</em>{" "}
                        row.
                      </li>
                      <li style={{ marginBottom: "0" }}>
                        If a Bispecific therapy was used as bridging therapy for
                        CAR-T, this is not considered combination therapy.
                        Please abstract this as a standalone cohort on its own
                        cohort not as a combination agent for the CAR-T therapy.
                      </li>
                      <li style={{ marginBottom: "0" }}>
                        <u><b>UPDATE AS OF 8/1/2026:</b></u> the eMMpower study will now
                        restrict Tec combination therapy to Tec + Daratumumab
                        and restrict Tal combination therapy to receipt of Tal +
                        Daratumumab or Tal + Dartumumab + Pomalidomide. Patients
                        receiving these combination regimens will be considered
                        ineligible if received in combination with any other
                        agents except corticosteroids.
                        <ol
                          style={{
                            margin: "8px 0 0 0",
                            paddingLeft: "30px",
                            fontSize: "16px",
                            listStyleType: "lower-alpha",
                          }}
                        >
                          <li style={{ marginBottom: "4px" }}>
                            E.g., Tec + Daratumuamb + Bortezomib would be
                            considered {" "}<u><i>ineligible</i></u>{" "} due to the addition of
                            Bortezomib with the Tec + Daratumumab combination
                            regimen
                          </li>
                          <li style={{ marginBottom: "0" }}>
                            E.g., Tal + Daratumumab + Pomalidomide +
                            Dexamethasone would be considered {" "}<u><i>eligible</i></u>{" "} as the
                            combination regimen was only received with a
                            corticosteroid
                          </li>
                        </ol>
                      </li>
                    </ol>

                    {eligibilityWarning && (
                      <div style={{ marginTop: "10px" }}>
                        <ErrorField
                          show={true}
                          messageAnswer
                          message={EligibilityCriteria_Msgs.eligibilityWarning}
                        />
                      </div>
                    )}
                  </div>

                  <div className="question-answer">
                    {bispecificTreatments &&
                      bispecificTreatments.length > 0 && (
                        <>
                          {bispecificTreatments.map(
                            (treatment, treatmentIdx) => (
                              <div
                                key={`treatment-${treatment.cohortTreatmentID}`}
                                style={{ marginBottom: "30px" }}
                              >
                                <div
                                  className="cohort-header"
                                  style={{ marginBottom: "15px" }}
                                >
                                  <span
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {treatment.cohortTreatmentName}
                                    {treatment.superScriptText && (
                                      <sup className="superscript">
                                        {treatment.superScriptText.slice(0, 2)}
                                      </sup>
                                    )}
                                    {treatment.superScriptText &&
                                      treatment.superScriptText.slice(2)}
                                  </span>
                                </div>

                                <div className="question-bot-space">
                                  <div
                                    style={{
                                      overflowX: "auto",
                                      marginTop: "15px",
                                    }}
                                  >
                                    <table
                                      style={{
                                        border: "1px solid #333",
                                        borderCollapse: "collapse",
                                        width: "100%",
                                        minWidth: "600px",
                                      }}
                                    >
                                      <thead>
                                        <tr
                                          style={{
                                            backgroundColor: "#f5f5f5",
                                          }}
                                        >
                                          <th
                                            style={{
                                              border: "1px solid #333",
                                              padding: "10px",
                                              textAlign: "center",
                                              width: "40%",
                                            }}
                                          >
                                            Combination agent used
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid #333",
                                              padding: "10px",
                                              textAlign: "center",
                                              width: "40%",
                                            }}
                                          >
                                            Date of Treatment Initiation
                                          </th>
                                          <th
                                            style={{
                                              border: "1px solid #333",
                                              padding: "10px",
                                              textAlign: "center",
                                              width: "10%",
                                            }}
                                          >
                                            Action
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {treatment.agentRows.map(
                                          (row, rowIdx) => (
                                            <tr
                                              key={`row-${treatmentIdx}-${row.rowId}`}
                                            >
                                              <td
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: "8px",
                                                    alignItems: "flex-start",
                                                    flexWrap: "wrap",
                                                  }}
                                                >
                                                  <select
                                                    value={row.agentid || ""}
                                                    onChange={(e) =>
                                                      handleAgentChange(
                                                        treatmentIdx,
                                                        rowIdx,
                                                        e.target.value
                                                          ? Number(
                                                              e.target.value,
                                                            )
                                                          : null,
                                                      )
                                                    }
                                                    style={{
                                                      minWidth: "100%",
                                                      padding: "8px",
                                                      borderRadius: "4px",
                                                      border: row.agentError
                                                        ? "2px solid #d32f2f"
                                                        : "1px solid #ccc",
                                                      fontSize: "13px",
                                                    }}
                                                  >
                                                    <option value="">
                                                      -- Select Agent --
                                                    </option>
                                                    {Object.entries(
                                                      getGroupedAgents(
                                                        treatmentIdx,
                                                        rowIdx,
                                                      ),
                                                    ).map(
                                                      ([category, agents]) => (
                                                        <optgroup
                                                          key={category}
                                                          label={category}
                                                        >
                                                          {sortAgents(
                                                            agents,
                                                          ).map((agent) => (
                                                            <option
                                                              key={
                                                                agent.agentid
                                                              }
                                                              value={
                                                                agent.agentid
                                                              }
                                                            >
                                                              {agent.agentName}
                                                            </option>
                                                          ))}
                                                        </optgroup>
                                                      ),
                                                    )}
                                                  </select>

                                                  {isOtherAgentSelected(
                                                    row.agentid,
                                                    agentList,
                                                  ) && (
                                                    <input
                                                      type="text"
                                                      value={
                                                        row.agentOtherValue
                                                      }
                                                      onChange={(e) =>
                                                        handleAgentOtherChange(
                                                          treatmentIdx,
                                                          rowIdx,
                                                          e.target.value,
                                                        )
                                                      }
                                                      placeholder="please specify"
                                                      style={{
                                                        minWidth: "200px",
                                                        padding: "6px 8px",
                                                        borderRadius: "4px",
                                                        border: row.agentError
                                                          ? "2px solid #d32f2f"
                                                          : "1px solid #ccc",
                                                        fontSize: "12px",
                                                        flex: "1",
                                                      }}
                                                    />
                                                  )}
                                                </div>

                                                {row.agentError && (
                                                  <div
                                                    style={{
                                                      color: "#d32f2f",
                                                      fontSize: "11px",
                                                      marginTop: "4px",
                                                    }}
                                                  >
                                                    Please choose one of the
                                                    provided options
                                                  </div>
                                                )}
                                              </td>

                                              <td
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      gap: "8px",
                                                      alignItems: "center",
                                                      flexWrap: "wrap",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      name={`date-${treatmentIdx}-${rowIdx}`}
                                                      value="1"
                                                      checked={
                                                        row.initiationDateRadio ===
                                                        1
                                                      }
                                                      onChange={() =>
                                                        handleDateRadioChange(
                                                          treatmentIdx,
                                                          rowIdx,
                                                          1,
                                                        )
                                                      }
                                                      style={{
                                                        marginRight: "0px",
                                                      }}
                                                    />
                                                    <DatePickerComponent
                                                      ctrlId={`date-${treatmentIdx}-${rowIdx}`}
                                                      date={row.initiationDate}
                                                      HandleDateChange={(
                                                        name,
                                                        date,
                                                      ) =>
                                                        handleDateChange(
                                                          treatmentIdx,
                                                          rowIdx,
                                                          date,
                                                        )
                                                      }
                                                      minDate={dayjs(
                                                        "01-01-1920",
                                                      )}
                                                      maxDate={dayjs()}
                                                      locale="en"
                                                    />
                                                  </div>

                                                  <label
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      fontSize: "12px",
                                                      margin: "0px",
                                                    }}
                                                  >
                                                    <input
                                                      type="radio"
                                                      name={`date-${treatmentIdx}-${rowIdx}`}
                                                      value="99"
                                                      checked={
                                                        row.initiationDateRadio ===
                                                        99
                                                      }
                                                      onChange={() =>
                                                        handleDateRadioChange(
                                                          treatmentIdx,
                                                          rowIdx,
                                                          99,
                                                        )
                                                      }
                                                      style={{
                                                        marginRight: "6px",
                                                      }}
                                                    />
                                                    Date of treatment initiation
                                                    unknown
                                                  </label>
                                                </div>
                                                <ErrorField
                                                  show={row.dateError}
                                                  message={
                                                    "Please enter the date of treatment initiation (if known) or select unknown otherwise"
                                                  }
                                                />

                                                <ErrorField
                                                  show={row.invalidDateError}
                                                  message={
                                                    "Invalid Date. Please enter a valid date and ensure that the date is not in the future."
                                                  }
                                                />
                                              </td>

                                              <td
                                                style={{
                                                  border: "1px solid #333",
                                                  padding: "10px",
                                                  textAlign: "center",
                                                  verticalAlign: "middle",
                                                }}
                                              >
                                                <img
                                                  src="./Assets/images/Icon-material-delete.png"
                                                  alt="Delete"
                                                  width="18px"
                                                  style={{
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() =>
                                                    deleteAgentRow(
                                                      treatmentIdx,
                                                      rowIdx,
                                                    )
                                                  }
                                                />
                                              </td>
                                            </tr>
                                          ),
                                        )}

                                        {canShowAddMore(treatmentIdx) && (
                                          <tr>
                                            <td
                                              colSpan="3"
                                              style={{
                                                border: "1px solid #333",
                                                padding: "10px",
                                                textAlign: "left",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <img
                                                  src="./Assets/images/plus.png"
                                                  alt="Add"
                                                  width="18px"
                                                  style={{
                                                    marginRight: "6px",
                                                  }}
                                                />
                                                <a
                                                  onClick={() =>
                                                    addAgentRow(treatmentIdx)
                                                  }
                                                  style={{
                                                    color: "#2196F3",
                                                    textDecoration: "none",
                                                    fontSize: "13px",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  Click to add another agent
                                                </a>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>

                                  {treatmentErrors[treatmentIdx] && (
                                    <div>
                                      <ErrorField
                                        show={true}
                                        message={`Please fill data for ${treatment.cohortTreatmentName}`}
                                      />
                                    </div>
                                  )}

                                  {cohortTerminate[treatmentIdx] && (
                                    <div style={{ marginTop: "8px" }}>
                                      <ErrorField
                                        show={true}
                                        message={`This patient does not meet the eligibility criteria for this treatment cohort.`}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ),
                          )}
                        </>
                      )}
                  </div>
                </div>
              </div>
              <div className="survey-section-content-foot">
                <SaveAndNavigate paths={navigatePaths} Save={SavePage} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BispecificInitiation;
